// @tier: PREMIUM — Dashboard analytique admin uniquement
// Route /dashboard protégée par SubscriptionRoute requiredSubscription="premium" dans App.jsx.
// Page unifiée à 5 onglets — fusionne l'ancien /dashboard (KPIs), /dashboard-premium (bus temps
// réel, flotte, parents, notifications avancés — route orpheline, jamais liée au menu ni déclarée
// dans features.js, donc inaccessible en prod) et /reports (ponctualité/présence/chauffeurs/
// incidents + export). Voir CLAUDE.md pour le détail de cette fusion.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bus,
  Users,
  UserCheck,
  MapPin,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Activity,
  BarChart3,
  UserPlus,
  Settings,
  Info,
  Shield,
  Bell,
  Download,
  FileText,
  Calendar as CalendarIcon,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useDashboardReal, { useFleetStats, useParentsDetail, useNotificationsTimeline } from '../hooks/trDashboard';
import useReports, { REPORT_COLORS } from '../hooks/trReports';
import PageError from '../components/PageError';

const iconMap = {
  Bus,
  Users,
  UserCheck,
  MapPin,
  AlertTriangle,
  UserPlus,
  CheckCircle,
  Clock,
  XCircle,
  Settings,
  Info,
  Activity,
  BarChart3,
};

// "2026-08-20" → "20/08" pour les axes de graphique
const formatDayTick = (isoDate) => {
  const [, month, day] = isoDate.split('-');
  return `${day}/${month}`;
};

const Dashboard = () => {
  const {
    stats,
    busStatus,
    recentActivities,
    loading,
    error,
    refreshing,
    lastUpdated,
    refresh,
    getStatusColor,
    getActivityColor,
    formattedStats,
    systemHealth,
    hasData,
    isDataFresh,
    parentStats,
    notificationStats,
    incidents = [],
  } = useDashboardReal();

  const { data: fleetStats, refetch: refetchFleetStats } = useFleetStats();
  const { data: parentsDetail, refetch: refetchParentsDetail } = useParentsDetail();
  const { data: notificationsTimeline, refetch: refetchNotificationsTimeline } = useNotificationsTimeline();

  const {
    dateRange,
    setDateRange,
    selectedCircuit,
    setSelectedCircuit,
    circuits,
    punctualityData,
    attendanceData,
    driversData,
    incidentsData,
    punctualityByCircuit,
    driverPerformance,
    incidentTypes,
    bestCircuitInfo,
    worstCircuitInfo,
    handleExportPDF,
    handleExportExcel,
    refresh: refreshReports,
  } = useReports();

  const refreshAll = () => {
    refresh();
    refreshReports();
    refetchFleetStats();
    refetchParentsDetail();
    refetchNotificationsTimeline();
  };

  if (loading && !hasData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto" />
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
          <p className="text-sm text-gray-500 mt-2">Récupération des données en temps réel</p>
        </div>
      </div>
    );
  }

  if (error && !hasData) {
    return <PageError variant="fullpage" error={error} onRetry={refresh} />;
  }

  const avgDriverCompletion = driversData?.drivers?.length
    ? Math.round(driversData.drivers.reduce((s, d) => s + (d.completion_rate ?? 0), 0) / driversData.drivers.length)
    : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            Tableau de bord
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-1">
            <p className="text-gray-600">Vue d'ensemble de votre système de transport scolaire</p>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isDataFresh ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className="text-xs text-gray-500">{isDataFresh ? 'Données à jour' : 'Données en cache'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Shield className="w-4 h-4" />
            <span>Santé système: {systemHealth}%</span>
          </div>
          <button
            onClick={refreshAll}
            disabled={refreshing}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualiser les données"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filtres — période et circuit, communs aux onglets Flotte/Parents/Notifications/Export */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
          <CardDescription>Période et circuit analysés dans les onglets ci-dessous</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Période</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'dd MMM', { locale: fr })} -{' '}
                          {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
                        </>
                      ) : (
                        format(dateRange.from, 'dd MMM yyyy', { locale: fr })
                      )
                    ) : (
                      <span>Sélectionner une période</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={2}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Circuit</label>
              <Select value={selectedCircuit} onValueChange={setSelectedCircuit}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les circuits" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les circuits</SelectItem>
                  {circuits.map((circuit) => (
                    <SelectItem key={circuit.id} value={circuit.id}>
                      {circuit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="fleet">Flotte</TabsTrigger>
          <TabsTrigger value="parents">Parents & Élèves</TabsTrigger>
          <TabsTrigger value="notifications">Notifications & Incidents</TabsTrigger>
          <TabsTrigger value="export">Export</TabsTrigger>
        </TabsList>

        {/* ─── Onglet 1 : Vue d'ensemble ──────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {formattedStats.map((stat, index) => {
              const Icon = iconMap[stat.icon] || Activity;
              const isPositive = stat.change.startsWith('+');
              const isNegative = stat.change.startsWith('-') && stat.change !== '0';
              return (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card-brand p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                      <div className="flex items-center mt-2">
                        {isPositive ? (
                          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                        ) : isNegative ? (
                          <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                        ) : (
                          <Activity className="w-4 h-4 text-gray-500 mr-1" />
                        )}
                        <span
                          className={`text-sm ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'}`}
                        >
                          {stat.change}
                        </span>
                        {stat.total && <span className="text-xs text-gray-500 ml-2">/ {stat.total} total</span>}
                      </div>
                    </div>
                    <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                      <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="card-brand">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">État des bus</h3>
                </div>
                <div className="p-4 space-y-3">
                  {busStatus.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Bus className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">Aucun bus en circulation actuellement</p>
                    </div>
                  ) : (
                    busStatus.map((bus, index) => (
                      <motion.div
                        key={bus.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center gradient-blue">
                            <Bus className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {bus.bus_number || bus.license_plate || bus.id}
                            </h4>
                            <p className="text-sm text-gray-600">{bus.route}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bus.status)}`}
                          >
                            {bus.status}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {bus.students != null ? `${bus.students}/` : ''}
                            {bus.capacity ?? '—'} places
                          </p>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="card-brand">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Activités récentes</h3>
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                </div>
                <div className="p-6 space-y-4">
                  {recentActivities.map((activity, index) => {
                    const Icon = iconMap[activity.icon] || Info;
                    return (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start space-x-3"
                      >
                        <div className={`p-1 rounded-full ${getActivityColor(activity.type)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">{activity.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-gray-500">{activity.time}</p>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                activity.type === 'success'
                                  ? 'bg-green-100 text-green-700'
                                  : activity.type === 'warning'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : activity.type === 'error'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {activity.category}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {stats?.pending_requests !== undefined && (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>Demandes en attente</CardTitle>
                  <Link to="/admin/requests" className="text-sm text-blue-600 hover:underline">
                    Voir les demandes
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <span
                      data-testid="pending-requests-badge"
                      className={`text-3xl font-bold ${stats.pending_requests > 5 ? 'text-red-600' : 'text-gray-900'}`}
                    >
                      {stats.pending_requests}
                    </span>
                    <p className="text-sm text-gray-600 mt-1">En attente</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">
                      {stats.approval_rate !== null ? `${stats.approval_rate}%` : '—'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Taux d'approbation</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-xl font-semibold text-gray-700 capitalize">{stats.top_request_type ?? '—'}</p>
                    <p className="text-sm text-gray-600 mt-1">Type le plus fréquent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Onglet 2 : Flotte ──────────────────────────────────────────── */}
        <TabsContent value="fleet" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Flotte — 30 derniers jours</CardTitle>
              <CardDescription>Trajets par jour, durée moyenne, arrêts desservis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-900">{fleetStats?.avg_duration_min ?? '—'}</p>
                  <p className="text-xs text-gray-600 mt-1">min / trajet</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-green-600">{fleetStats?.stops_served ?? 0}</p>
                  <p className="text-xs text-gray-600 mt-1">arrêts desservis</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-orange-600">{fleetStats?.stops_skipped ?? 0}</p>
                  <p className="text-xs text-gray-600 mt-1">arrêts skippés</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-900">
                    {fleetStats?.completed_trips ?? 0}/{fleetStats?.total_trips ?? 0}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">trajets complétés</p>
                </div>
              </div>
              {fleetStats?.trips_per_day?.length > 0 && (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={fleetStats.trips_per_day}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDayTick} minTickGap={24} fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip labelFormatter={formatDayTick} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="total"
                      name="Trajets"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.15}
                    />
                    <Area
                      type="monotone"
                      dataKey="completed"
                      name="Complétés"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.25}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {stats?.on_time_rate !== undefined && (
            <Card>
              <CardHeader>
                <CardTitle>Indicateurs de service</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">
                      {stats.on_time_rate !== null ? `${stats.on_time_rate}%` : '—'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Ponctualité (globale)</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <span
                      data-testid="gps-alerts-badge"
                      className={`text-3xl font-bold ${(stats.offline_gps_alerts ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}
                    >
                      {stats.offline_gps_alerts ?? 0}
                    </span>
                    <p className="text-sm text-gray-600 mt-1">Alertes GPS hors ligne</p>
                  </div>
                  <div className="col-span-1 sm:col-span-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Top 3 arrêts en retard</p>
                    {(stats.top3_late_stops?.length ?? 0) === 0 ? (
                      <p className="text-sm text-gray-500">Aucun retard enregistré</p>
                    ) : (
                      <ol className="space-y-1">
                        {stats.top3_late_stops.map((stop, i) => (
                          <li key={stop.stop_id} className="flex justify-between text-sm">
                            <span className="text-gray-700">
                              <span className="text-gray-400 mr-1">{i + 1}.</span>
                              <span>{stop.name}</span>
                            </span>
                            <span className="text-orange-600 font-medium">+{stop.avg_delay_min} min</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Ponctualité par circuit</CardTitle>
            </CardHeader>
            <CardContent>
              {punctualityByCircuit.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">Aucune donnée pour la période sélectionnée</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={punctualityByCircuit}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="onTime" fill="#10B981" name="À l'heure" />
                      <Bar dataKey="delayed" fill="#F59E0B" name="En retard" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="flex items-center text-sm font-medium text-green-700 mb-1">
                        <TrendingUp className="h-4 w-4 mr-1" /> Meilleur circuit
                      </p>
                      <p className="text-lg font-bold">{bestCircuitInfo?.name ?? '—'}</p>
                      <p className="text-sm text-gray-600">
                        {bestCircuitInfo?.rate != null ? `${bestCircuitInfo.rate}% de ponctualité` : '—'}
                      </p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="flex items-center text-sm font-medium text-orange-700 mb-1">
                        <AlertTriangle className="h-4 w-4 mr-1" /> Circuit à améliorer
                      </p>
                      <p className="text-lg font-bold">{worstCircuitInfo?.name ?? '—'}</p>
                      <p className="text-sm text-gray-600">
                        {worstCircuitInfo?.rate != null ? `${worstCircuitInfo.rate}% de ponctualité` : '—'}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {driversData?.drivers?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Performances chauffeurs</CardTitle>
                <CardDescription>Taux de complétion par chauffeur sur la période</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Vue cartes — mobile uniquement */}
                <div className="md:hidden divide-y divide-gray-100">
                  {driverPerformance.map((d) => (
                    <div key={d.id ?? d.name} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{d.name}</p>
                        <p className="text-sm text-gray-500">{d.trips} trajets</p>
                      </div>
                      <span className={`font-semibold ${d.rating >= 90 ? 'text-green-600' : 'text-orange-600'}`}>
                        {d.rating}%
                      </span>
                    </div>
                  ))}
                </div>

                {/* Tableau — desktop uniquement */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-[420px] w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="py-2 pr-4 font-medium">Chauffeur</th>
                        <th className="py-2 pr-4 font-medium text-right">Trajets</th>
                        <th className="py-2 font-medium text-right">Taux complétion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverPerformance.map((d) => (
                        <tr key={d.id ?? d.name} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-2 pr-4 text-gray-900 font-medium">{d.name}</td>
                          <td className="py-2 pr-4 text-right text-gray-600">{d.trips}</td>
                          <td className="py-2 text-right">
                            <span className={`font-semibold ${d.rating >= 90 ? 'text-green-600' : 'text-orange-600'}`}>
                              {d.rating}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Onglet 3 : Parents & Élèves ────────────────────────────────── */}
        <TabsContent value="parents" className="space-y-6">
          {parentStats && (
            <Card>
              <CardHeader>
                <CardTitle>Parents actifs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Actifs aujourd'hui", value: parentStats.active_today ?? 0, color: 'text-green-600' },
                    { label: 'Actifs ce mois', value: parentStats.active_month ?? 0, color: 'text-blue-600' },
                    { label: 'Total inscrits', value: parentStats.total ?? 0, color: 'text-gray-700' },
                    { label: 'Dormants (30j)', value: parentStats.dormant ?? 0, color: 'text-orange-600' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className={`text-3xl font-bold ${color}`}>{value}</p>
                      <p className="text-sm text-gray-600 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {attendanceData && (
            <Card>
              <CardHeader>
                <CardTitle>Présence élèves</CardTitle>
                <CardDescription>Sur la période sélectionnée</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">
                      {attendanceData.boarding_rate !== null ? `${attendanceData.boarding_rate}%` : '—'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Taux de présence</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{attendanceData.boarded ?? 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Présents</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-gray-700">{attendanceData.total_records ?? 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Total enregistrements</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {parentsDetail?.parents?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Activation & engagement</CardTitle>
                <CardDescription>Statut d'activation et dernière connexion (30 derniers jours)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-lg font-bold text-green-600">{parentsDetail.activated}</p>
                    <p className="text-xs text-gray-600 mt-1">activés</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-lg font-bold text-orange-600">{parentsDetail.pending}</p>
                    <p className="text-xs text-gray-600 mt-1">en attente</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-bold text-gray-700">{parentsDetail.dormant}</p>
                    <p className="text-xs text-gray-600 mt-1">dormants (30j)</p>
                  </div>
                </div>

                {/* Vue cartes — mobile uniquement */}
                <div className="md:hidden divide-y divide-gray-100">
                  {parentsDetail.parents.slice(0, 10).map((p) => (
                    <div key={p.id} className="py-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900">{p.name}</p>
                        <span className="text-xs text-gray-500">
                          {p.last_seen ? new Date(p.last_seen).toLocaleDateString('fr-FR') : '—'}
                        </span>
                      </div>
                      <div className="mt-1">
                        <Badge
                          variant="outline"
                          className={
                            p.activated
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : 'bg-gray-100 text-gray-700 border-gray-200'
                          }
                        >
                          {p.activated ? 'Activé' : 'En attente'}
                        </Badge>
                        {p.dormant && (
                          <Badge variant="outline" className="ml-1 bg-orange-100 text-orange-800 border-orange-200">
                            Dormant
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tableau — desktop uniquement */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-[420px] w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="py-2 pr-4 font-medium">Parent</th>
                        <th className="py-2 pr-4 font-medium">Statut</th>
                        <th className="py-2 font-medium text-right">Dernière connexion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parentsDetail.parents.slice(0, 10).map((p) => (
                        <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-2 pr-4 text-gray-900 font-medium">{p.name}</td>
                          <td className="py-2 pr-4">
                            <Badge
                              variant="outline"
                              className={
                                p.activated
                                  ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-gray-100 text-gray-700 border-gray-200'
                              }
                            >
                              {p.activated ? 'Activé' : 'En attente'}
                            </Badge>
                            {p.dormant && (
                              <Badge variant="outline" className="ml-1 bg-orange-100 text-orange-800 border-orange-200">
                                Dormant
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 text-right text-gray-600">
                            {p.last_seen ? new Date(p.last_seen).toLocaleDateString('fr-FR') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Onglet 4 : Notifications & Incidents ──────────────────────── */}
        <TabsContent value="notifications" className="space-y-6">
          {notificationStats && (
            <Card>
              <CardHeader>
                <CardTitle>Délivrabilité push</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-gray-900">{notificationStats.sent ?? 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Envoyées</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{notificationStats.delivered ?? 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Délivrées</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <span
                      data-testid="delivery-rate-badge"
                      className={`text-3xl font-bold ${(notificationStats.delivery_rate ?? 100) < 80 ? 'text-red-600' : 'text-blue-600'}`}
                    >
                      {notificationStats.delivery_rate !== null ? `${notificationStats.delivery_rate}%` : '—'}
                    </span>
                    <p className="text-sm text-gray-600 mt-1">Taux délivrance</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-600">
                      {notificationStats.open_rate !== null ? `${notificationStats.open_rate}%` : '—'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Taux d'ouverture</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Notifications — 30 derniers jours</CardTitle>
              <CardDescription>Envoyées, délivrées et ouvertes par jour</CardDescription>
            </CardHeader>
            <CardContent>
              {notificationsTimeline?.timeline?.some((d) => d.sent > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={notificationsTimeline.timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={formatDayTick} minTickGap={24} fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip labelFormatter={formatDayTick} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="sent"
                      name="Envoyées"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.1}
                    />
                    <Area
                      type="monotone"
                      dataKey="delivered"
                      name="Délivrées"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.15}
                    />
                    <Area
                      type="monotone"
                      dataKey="opened"
                      name="Ouvertes"
                      stroke="#8B5CF6"
                      fill="#8B5CF6"
                      fillOpacity={0.2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Aucune notification sur les 30 derniers jours</p>
                </div>
              )}
            </CardContent>
          </Card>

          {stats?.open_incidents !== undefined && (
            <Card>
              <CardHeader>
                <CardTitle>Incidents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <span
                      data-testid="open-incidents-badge"
                      className={`text-3xl font-bold ${(stats.open_incidents ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}
                    >
                      {stats.open_incidents ?? 0}
                    </span>
                    <p className="text-sm text-gray-600 mt-1">Incidents ouverts</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-600">{stats.weekly_incident_count ?? 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Cette semaine</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">
                      {stats.avg_resolution_min !== null && stats.avg_resolution_min !== undefined
                        ? `${stats.avg_resolution_min} min`
                        : '—'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Résolution moy.</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-xl font-semibold text-gray-700 capitalize">{stats.top_incident_type ?? '—'}</p>
                    <p className="text-sm text-gray-600 mt-1">Type dominant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Incidents récents</CardTitle>
                <CardDescription>Alertes et incidents signalés</CardDescription>
              </CardHeader>
              <CardContent>
                {incidents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p>Aucun incident signalé aujourd'hui</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {incidents.map((incident) => (
                      <div key={incident.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <AlertTriangle
                              className={`h-5 w-5 ${
                                incident.severity === 'high'
                                  ? 'text-red-600'
                                  : incident.severity === 'medium'
                                    ? 'text-orange-600'
                                    : 'text-yellow-600'
                              }`}
                            />
                            <span className="font-medium text-gray-900">{incident.bus_id || incident.busNumber}</span>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              incident.status === 'resolved'
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-orange-100 text-orange-800 border-orange-200'
                            }
                          >
                            {incident.status === 'resolved' ? 'Résolu' : 'En cours'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700">{incident.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Incidents par type</CardTitle>
              </CardHeader>
              <CardContent>
                {(incidentsData?.summary?.total ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">Aucun incident sur la période</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={incidentTypes}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={90}
                        dataKey="value"
                      >
                        {incidentTypes.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={REPORT_COLORS[index % REPORT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                {incidentTypes.length > 0 && (incidentsData?.summary?.total ?? 0) > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {incidentTypes.map((type, index) => (
                      <li key={type.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: REPORT_COLORS[index % REPORT_COLORS.length] }}
                          />
                          <span className="text-gray-700">{type.name}</span>
                        </span>
                        <span className="font-medium text-gray-900">{type.value}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── Onglet 5 : Export ──────────────────────────────────────────── */}
        <TabsContent value="export" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                type: 'punctuality',
                label: 'Ponctualité',
                value: punctualityData?.on_time_rate != null ? `${punctualityData.on_time_rate}%` : '—',
                sub: `${punctualityData?.total_stops ?? 0} arrêts analysés`,
              },
              {
                type: 'attendance',
                label: 'Présences',
                value: attendanceData?.boarding_rate != null ? `${attendanceData.boarding_rate}%` : '—',
                sub: `${attendanceData?.boarded ?? 0}/${attendanceData?.total_records ?? 0} présents`,
              },
              {
                type: 'drivers',
                label: 'Chauffeurs',
                value: avgDriverCompletion != null ? `${avgDriverCompletion}%` : '—',
                sub: `${driversData?.drivers?.length ?? 0} chauffeurs actifs`,
              },
              {
                type: 'incidents',
                label: 'Incidents',
                value: incidentsData?.summary?.total ?? 0,
                sub: `${incidentsData?.summary?.open ?? 0} ouverts`,
              },
            ].map(({ type, label, value, sub }) => (
              <Card key={type}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{value}</div>
                  <p className="text-xs text-gray-500 mt-1">{sub}</p>
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => handleExportPDF(type)}>
                      <FileText className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => handleExportExcel(type)}>
                      <Download className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      {lastUpdated && (
        <div className="text-center text-xs text-gray-500">
          Dernière mise à jour: {new Date(lastUpdated).toLocaleString('fr-FR')}
          {error && <span className="ml-2 text-yellow-600">(Mode dégradé - Données de fallback)</span>}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
